import AIButton from '@cobliteam/ai/src/components/AIButton';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import Icon from '@cobliteam/ui/icons';
import { formatRelativeDateToNow } from '@cobliteam/utils/date';
import NotificationCardShell, { type NotificationCardTheme } from '../../core/NotificationCardShell';
import { formatRiskRecognitionCardDisplay, getRiskRecognitionIcon } from './format';
import RiskEvidence from './RiskEvidence';
import RiskHighlightText, { renderRiskHighlight } from './RiskHighlightText';

// Tema do card de risco (danger/vermelho + borda animada). Reproduz exatamente o visual anterior do
// RiskRecognitionCardContent — só que agora via o shell compartilhado, para o toast e o card flutuante
// (e tipos futuros) reusarem a mesma estrutura.
const RISK_CARD_THEME: NotificationCardTheme = {
  containerClassName: 'border-danger-5 bg-[#fcfdfe] shadow-[0_0_0_3px_rgba(254,226,226,0.70),0_12px_28px_-16px_rgba(185,28,28,0.48),0_6px_14px_-10px_rgba(15,23,42,0.18)]',
  shineColors: ['#FECACA', '#FCA5A5', '#EF4444', '#DC2626', '#F87171', '#FECACA'],
  iconWrapClassName: 'bg-danger-3 text-danger-9',
  titleClassName: 'text-danger-10',
};

interface RiskCardBodyProps {
  highlight: RiskRecognitionHighlight;
  actionLabel?: string | null;
  dismissLabel?: string | null;
  onDismiss: () => void;
  onAction: () => void;
  className?: string;
}

// Corpo do card de risco: monta cópia/ícone/CTA e delega a moldura visual ao NotificationCardShell.
// Compartilhado entre o card flutuante (/monitoring) e o toast (sonner, qualquer página).
const RiskCardBody = ({ highlight, actionLabel, dismissLabel, onDismiss, onAction, className }: RiskCardBodyProps) => {
  const { t } = useTranslation('notifications');
  const { headline, description, location } = formatRiskRecognitionCardDisplay(highlight);
  const iconName = getRiskRecognitionIcon(highlight);
  const actionText = actionLabel ?? t('risk_recognition.action');
  const dismissText = dismissLabel ?? t('risk_recognition.dismiss');

  return (
    <NotificationCardShell
      theme={RISK_CARD_THEME}
      iconName={iconName}
      className={className}
      badge={<Tag variant='surface' color='danger' size='sm' label={t('risk_recognition.badge')} icon={<Icon name='triangle-alert' size='xs' />} />}
      title={renderRiskHighlight(headline, 'font-semibold')}
      description={<RiskHighlightText text={description} variant='description-list-default' className='break-words text-gray-11 leading-[1.45] [overflow-wrap:anywhere]' />}
      media={<RiskEvidence highlight={highlight} surface='toast' size='md' onOpen={() => onAction()} />}
      location={location}
      timestamp={formatRelativeDateToNow(highlight.created_at, { addSuffix: true })}
      dismissLabel={dismissText}
      onDismiss={onDismiss}
      action={<AIButton size='sm' label={actionText} shape='cta' showTooltip={false} onClick={onAction} />}
    />
  );
};

export default RiskCardBody;
